import React, { useMemo } from 'react';
import { Copy, Check, Link2, Hash } from 'lucide-react';
import { sanitizeHtml } from '../../utils/textProcessing.js';
import SourcesCitedBlock from './SourcesCitedBlock.jsx';
import { SOCIAL_CHANNELS } from '../../constants/channels.js';

// Instagram brand pink/magenta. Lookup is single-sourced from SOCIAL_CHANNELS so
// a future re-skin updates one place; fall back to literal if the constant moves.
const IG_COLOR =
  (SOCIAL_CHANNELS.find((c) => c.id === 'instagram') || {}).color || '#E4405F';

// QA bar color thresholds (mirrors NewsletterResultEntry / SeoResultEntry).
const qaColor = (score) =>
  score >= 70 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';

// Character-count budget — 5-tier per spec §6.
// Returns { label, color, fillRatio } for the budget bar UI.
// The bar's fill is uniformly capped at 1.0; over-budget is conveyed by the
// red color, not by overflowing the bar.
const budgetTier = (count) => {
  const n = Number.isFinite(count) ? count : 0;
  if (n > 2200) return { label: 'Oltre limite IG (≤2200)', color: '#EF4444', fillRatio: 1 };
  if (n > 1800) return { label: 'Vicino al limite IG',     color: '#F97316', fillRatio: n / 2200 };
  if (n > 1000) return { label: 'Caption lungo',            color: '#F59E0B', fillRatio: n / 2200 };
  if (n >  125) return { label: 'Zona engagement',          color: '#10B981', fillRatio: n / 2200 };
  return            { label: 'Solo hook',                   color: '#6B7280', fillRatio: n / 2200 };
};

// Paragraph splitter (defensive; BE body has \n\n breaks).
const splitParagraphs = (text) =>
  String(text || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

// Hashtag normalizer — prepend `#` if missing. Pass-through otherwise so a
// Writer-drift `##` stays visible (spec §10).
const renderTag = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  return s.startsWith('#') ? s : `#${s}`;
};

const SectionLabel = ({ children, color, t }) => (
  <div
    style={{
      fontSize: '10px',
      fontFamily: "'JetBrains Mono', monospace",
      color: color || t.textMuted || '#7a7a8e',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontWeight: 600,
      marginBottom: '8px',
    }}
  >
    {children}
  </div>
);

const CopyBtn = ({ active, onClick, label, t, color }) => (
  <button
    onClick={onClick}
    style={{
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '10.5px',
      fontWeight: 600,
      cursor: 'pointer',
      border: `1px solid ${t.border || 'rgba(0,0,0,0.1)'}`,
      background: 'transparent',
      color: color || t.textMuted || '#7a7a8e',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    }}
  >
    {active ? <><Check size={11} /> Copiato</> : <><Copy size={11} /> {label}</>}
  </button>
);

const InstagramResultEntry = ({
  entry,
  copiedId,
  onCopy,
  w = (o) => `rgba(255,255,255,${o})`,
  t = {},
}) => {
  const r = entry.results || {};
  const c = useMemo(() => r.content || {}, [r.content]);
  const color = IG_COLOR;
  const eId = entry.id || 'instagram';

  const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;

  const hook = (c.hook || '').trim();
  const body = (c.body || '').trim();
  const cta = (c.cta || '').trim();
  const hashtags = Array.isArray(c.hashtags) ? c.hashtags.filter(Boolean) : [];
  const ctaUrl = (c.cta_url || '').trim();

  const charCount = Number.isFinite(r.metadata?.character_count)
    ? r.metadata.character_count
    : (c.full_text || '').length;

  const tier = budgetTier(charCount);

  const bodyParas = useMemo(() => splitParagraphs(body), [body]);

  return (
    <div>
      {/* Region 1 — QA Score bar */}
      {qaScore != null && (
        <div style={{ marginBottom: '18px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <SectionLabel t={t}>QA Score</SectionLabel>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: qaColor(qaScore),
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {qaScore}/100
            </span>
          </div>
          <div
            style={{
              height: '6px',
              borderRadius: '4px',
              background: w(0.06),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, qaScore))}%`,
                height: '100%',
                background: qaColor(qaScore),
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Region 3 — Hook ("anteprima feed" tinted box) */}
      {hook && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
              gap: '8px',
            }}
          >
            <SectionLabel color={color} t={t}>
              Anteprima feed (hook)
            </SectionLabel>
            <CopyBtn
              active={copiedId === `ig-hook-${eId}`}
              onClick={() => onCopy?.(hook, `ig-hook-${eId}`)}
              label="Copia hook"
              t={t}
              color={color}
            />
          </div>
          <div
            style={{
              padding: '14px 16px',
              borderRadius: '10px',
              background: `${color}08`,
              borderLeft: `3px solid ${color}`,
              fontSize: '15px',
              fontWeight: 500,
              lineHeight: 1.55,
              color: t.text,
              whiteSpace: 'pre-wrap',
            }}
          >
            {hook}
          </div>
        </div>
      )}

      {/* Region 4 — Body paragraphs */}
      {bodyParas.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionLabel t={t}>Caption</SectionLabel>
          <div
            style={{
              padding: '14px 16px',
              background: w(0.02),
              border: `1px solid ${w(0.06)}`,
              borderRadius: '10px',
              color: t.text,
            }}
          >
            {bodyParas.map((p, i) => (
              <p
                key={i}
                style={{
                  margin: i === bodyParas.length - 1 ? 0 : '0 0 10px',
                  fontSize: '14px',
                  lineHeight: 1.7,
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(p) }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Region 5 — CTA (engagement question) */}
      {cta && (
        <div style={{ marginBottom: '14px' }}>
          <SectionLabel color={color} t={t}>CTA — Domanda engagement</SectionLabel>
          <div
            style={{
              padding: '12px 16px',
              background: `${color}08`,
              border: `1px solid ${color}20`,
              borderRadius: '10px',
              fontSize: '14px',
              lineHeight: 1.55,
              fontStyle: 'italic',
              color: t.text,
            }}
          >
            {cta}
          </div>
        </div>
      )}

      {/* Region 6 — Hashtag chips */}
      {hashtags.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            <Hash size={12} color={color} />
            <SectionLabel color={color} t={t}>
              PRIMO COMMENTO — HASHTAG ({hashtags.length})
            </SectionLabel>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {hashtags.map((tag, i) => (
              <span
                key={i}
                style={{
                  padding: '3px 10px',
                  borderRadius: '20px',
                  background: `${color}15`,
                  color,
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {renderTag(tag)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Region 7 — Link-in-bio pointer (non-clickable) */}
      {ctaUrl && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 14px',
            background: w(0.02),
            border: `1px dashed ${color}40`,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12.5px',
            color: t.textMuted || '#7a7a8e',
            flexWrap: 'wrap',
          }}
          title="Le caption Instagram non rendono cliccabili i link: indica 'link in bio' nel testo"
        >
          <Link2 size={13} color={color} />
          <span style={{ fontWeight: 600, color }}>Link in bio:</span>
          <code
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '11.5px',
              color: t.text,
              wordBreak: 'break-all',
              background: w(0.04),
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            {ctaUrl}
          </code>
        </div>
      )}

      {/* Region 8 — Character-count budget bar */}
      <div style={{ marginBottom: '14px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px',
          }}
        >
          <SectionLabel t={t}>Budget caption</SectionLabel>
          <span
            style={{
              fontSize: '12px',
              fontFamily: "'JetBrains Mono', monospace",
              color: tier.color,
              fontWeight: 700,
            }}
          >
            {charCount}/2200 — {tier.label}
          </span>
        </div>
        <div
          style={{
            height: '6px',
            borderRadius: '4px',
            background: w(0.06),
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.min(100, Math.max(0, tier.fillRatio * 100))}%`,
              height: '100%',
              background: tier.color,
              transition: 'width 0.4s ease',
            }}
          />
        </div>
      </div>

      {/* Region 10 — Sources cited (shared block) */}
      <SourcesCitedBlock
        sources={r.sources?.cited}
        copiedId={copiedId}
        onCopy={onCopy}
        entryId={eId}
        w={w}
        t={t}
      />
    </div>
  );
};

export default InstagramResultEntry;
